import * as React from 'react';

import { cn } from '@/shared/lib/utils/classnames';

const Textarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<'textarea'>>(
  ({ className, style, onFocus, onBlur, 'aria-invalid': ariaInvalid, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const combinedRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    // 에러 상태 감지 및 스타일 적용
    React.useEffect(() => {
      if (textareaRef.current) {
        const isInvalid =
          ariaInvalid === true || textareaRef.current.getAttribute('aria-invalid') === 'true';
        if (isInvalid) {
          // 에러 상태: red border
          textareaRef.current.style.setProperty('border-color', '#ef4444', 'important');
        } else if (
          !textareaRef.current.disabled &&
          document.activeElement !== textareaRef.current
        ) {
          // 에러가 아니고 포커스도 아닐 때: 기본 border 색상
          textareaRef.current.style.removeProperty('border-color');
        }
      }
    }, [ariaInvalid]);

    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        // 전역 CSS의 !important를 오버라이드하기 위해 직접 스타일 설정
        if (textareaRef.current) {
          const isInvalid =
            ariaInvalid === true || textareaRef.current.getAttribute('aria-invalid') === 'true';
          const computedStyle = window.getComputedStyle(textareaRef.current);
          // border-radius를 먼저 명시적으로 유지 (렌더링 문제 방지)
          const borderRadius = computedStyle.borderRadius || '6px';
          textareaRef.current.style.setProperty('border-radius', borderRadius, 'important');

          // Focus: 에러가 아니면 black border, 에러면 red border 유지
          // border-color만 변경하여 border-radius 렌더링 문제 방지
          if (!isInvalid) {
            textareaRef.current.style.setProperty('border-color', '#000000', 'important');
          } else {
            textareaRef.current.style.setProperty('border-color', '#ef4444', 'important');
          }
          // Ring, outline 완전히 제거
          textareaRef.current.style.setProperty('outline', 'none', 'important');
          textareaRef.current.style.setProperty('outline-width', '0', 'important');
          textareaRef.current.style.setProperty('outline-style', 'none', 'important');
          textareaRef.current.style.setProperty('outline-offset', '0', 'important');
          textareaRef.current.style.setProperty('box-shadow', 'none', 'important');
          textareaRef.current.style.setProperty('--tw-ring-offset-shadow', 'none', 'important');
          textareaRef.current.style.setProperty('--tw-ring-shadow', 'none', 'important');
        }
        onFocus?.(e);
      },
      [onFocus, ariaInvalid],
    );

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        // Blur 시 에러 상태에 따라 border 색상 설정
        if (textareaRef.current) {
          const isInvalid =
            ariaInvalid === true || textareaRef.current.getAttribute('aria-invalid') === 'true';
          const computedStyle = window.getComputedStyle(textareaRef.current);
          // border-radius를 먼저 명시적으로 유지 (렌더링 문제 방지)
          const borderRadius = computedStyle.borderRadius || '6px';
          textareaRef.current.style.setProperty('border-radius', borderRadius, 'important');

          if (isInvalid) {
            // 에러 상태: red border 유지
            textareaRef.current.style.setProperty('border-color', '#ef4444', 'important');
          } else {
            // 기본 상태: border 색상 제거 (기본값으로 복원)
            textareaRef.current.style.removeProperty('border-color');
          }
          // Ring, outline 제거 유지
          textareaRef.current.style.setProperty('outline', 'none', 'important');
          textareaRef.current.style.setProperty('box-shadow', 'none', 'important');
        }
        onBlur?.(e);
      },
      [onBlur, ariaInvalid],
    );

    return (
      <textarea
        className={cn(
          // 기본 스타일
          'flex min-h-[60px] w-full rounded-md border border-[#d9dde0] bg-white px-3 py-2 text-base text-black',
          // Placeholder 스타일 (light gray)
          'placeholder:text-[#9ca3af]',
          // Ring, outline 완전히 제거 (모든 상태에서)
          'outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
          // Disabled 상태: light gray background, light gray border
          'disabled:bg-[#f3f4f6] disabled:border-[#d9dde0] disabled:cursor-not-allowed disabled:text-[#9ca3af]',
          // Error 상태 (aria-invalid 속성 사용 시) - red border
          'aria-invalid:border-red-500',
          // Transition (border color만)
          'transition-colors',
          // 반응형
          'md:text-sm',
          className,
        )}
        style={{
          ...style,
          // 기본적으로 ring, outline 제거
          outline: 'none',
          boxShadow: 'none',
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-invalid={ariaInvalid}
        ref={combinedRef}
        {...props}
      />
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea };
